import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFacilityLayoutData } from "@context";
import {
  InAppNotification,
  listNotifications,
  markNotificationRead,
  markNotificationsRead,
  notificationNavigateTarget,
  notificationVariantLabel,
  notifyNetworkRequestsChanged,
  stashNetworkFocusFacilityId,
  stashNetworkRequestFocus,
  subscribeNotificationStream,
  type NotificationPushPayload,
} from "@services/notifications";
import {
  getFacilityHierarchyId,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
  type FacilityHierarchyInput,
} from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";

function findFacilityRowById(
  facilities: MyFacilityListRow[],
  facilityId: number,
): MyFacilityListRow | undefined {
  for (const row of facilities) {
    if (getFacilityHierarchyId(row as FacilityHierarchyInput) === facilityId) {
      return row;
    }
    for (const child of row.child_facilities || []) {
      if (getFacilityHierarchyId(child as FacilityHierarchyInput) === facilityId) {
        return child;
      }
    }
  }
  return undefined;
}

const SEEN_KEY = "unicomply.notification.seenIds";
const DROPDOWN_LIMIT = 8;

function readSeenIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((v) => Number(v)).filter((n) => Number.isFinite(n)));
  } catch {
    return new Set();
  }
}

function writeSeenIds(ids: Set<number>): void {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    // ignore
  }
}

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function pushToNotification(p: NotificationPushPayload): InAppNotification {
  return {
    id: Number(p.id),
    user_id: Number(p.user_id ?? 0),
    facility_id: p.facility_id == null ? null : Number(p.facility_id),
    variant: String(p.variant ?? ""),
    title: String(p.title ?? ""),
    body: String(p.body ?? ""),
    facility_name: null,
    npi: null,
    action_taken: "",
    action_taken_by: null,
    reason: null,
    is_read: Boolean(p.is_read),
    read_at: null,
    created_at: p.created_at ?? null,
  };
}

type NotificationBellProps = {
  active?: boolean;
};

export default function NotificationBell({ active = false }: NotificationBellProps) {
  const navigate = useNavigate();
  const { myFacilities } = useFacilityLayoutData();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const primedRef = useRef(false);
  const open = Boolean(anchorEl);

  const goToNotification = useCallback(
    (n: InAppNotification) => {
      const target = notificationNavigateTarget(n);
      const facilityId =
        n.facility_id != null && Number.isFinite(Number(n.facility_id))
          ? Number(n.facility_id)
          : null;
      stashNetworkRequestFocus(n);
      if (facilityId != null) {
        stashNetworkFocusFacilityId(facilityId);
        const match = findFacilityRowById(myFacilities, facilityId);
        if (match) {
          try {
            sessionStorage.setItem(
              SELECTED_FACILITY_STORAGE_KEY,
              JSON.stringify({ ...match, id: match.facility_id ?? match.id }),
            );
            notifySelectedFacilitySessionUpdated();
          } catch {
            // ignore
          }
        }
        if (target.state) {
          target.state = {
            ...target.state,
            selectedFacilityId: facilityId,
            selectedFacility: match
              ? {
                  ...match,
                  id: match.facility_id ?? match.id,
                  facility_id: match.facility_id,
                }
              : { facility_id: facilityId, id: facilityId },
          };
        }
      }
      navigate(
        { pathname: target.path, search: target.search ?? "" },
        target.state ? { state: target.state } : undefined,
      );
    },
    [myFacilities, navigate],
  );

  const refresh = useCallback(async (opts?: { toastNew?: boolean }) => {
    const toastNew = opts?.toastNew !== false;
    try {
      const result = await listNotifications({ limit: DROPDOWN_LIMIT, offset: 0 });
      setItems(result.notifications);
      setUnreadCount(result.unread_count);

      const seen = readSeenIds();
      const incoming = result.notifications;
      if (!primedRef.current) {
        incoming.forEach((n) => seen.add(n.id));
        writeSeenIds(seen);
        primedRef.current = true;
        return;
      }

      const fresh = incoming.filter((n) => !seen.has(n.id));
      if (toastNew && fresh.length > 0) {
        fresh
          .slice()
          .reverse()
          .forEach((n) => {
            toast.info(n.title || notificationVariantLabel(n.variant), {
              toastId: `notif-${n.id}`,
              autoClose: 6000,
              className: "figma-toast in-app-notification-toast",
              bodyClassName: "figma-toast-body",
              onClick: () => goToNotification(n),
            });
            seen.add(n.id);
          });
        writeSeenIds(seen);
      } else {
        incoming.forEach((n) => seen.add(n.id));
        writeSeenIds(seen);
      }
    } catch {
      // keep last known
    }
  }, [goToNotification]);

  const onPush = useCallback(
    (payload: NotificationPushPayload) => {
      const n = pushToNotification(payload);
      const seen = readSeenIds();
      if (seen.has(n.id)) return;
      seen.add(n.id);
      writeSeenIds(seen);
      setItems((prev) => [n, ...prev.filter((row) => row.id !== n.id)].slice(0, DROPDOWN_LIMIT));
      if (!n.is_read) {
        setUnreadCount((c) => c + 1);
      }
      toast.info(n.title || notificationVariantLabel(n.variant), {
        toastId: `notif-${n.id}`,
        autoClose: 6000,
        className: "figma-toast in-app-notification-toast",
        bodyClassName: "figma-toast-body",
        onClick: () => goToNotification(n),
      });
      notifyNetworkRequestsChanged(n.variant);
    },
    [goToNotification],
  );

  const refreshRef = useRef(refresh);
  const onPushRef = useRef(onPush);
  refreshRef.current = refresh;
  onPushRef.current = onPush;

  // Tab-wide singleton stream (survives StrictMode remount). One GET catch-up on ready.
  useEffect(() => {
    void refreshRef.current({ toastNew: false });
    return subscribeNotificationStream({
      onReady: () => {
        void refreshRef.current({ toastNew: false });
      },
      onNotification: (payload) => {
        onPushRef.current(payload);
      },
    });
  }, []);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    await refresh({ toastNew: false });
    setLoading(false);
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (n: InAppNotification) => {
    if (!n.is_read) {
      setItems((prev) =>
        prev.map((row) => (row.id === n.id ? { ...row, is_read: true } : row))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      void markNotificationRead(n.id)
        .then((res) => setUnreadCount(res.unread_count))
        .catch(() => undefined);
    }
    handleClose();
    goToNotification(n);
  };

  const handleMarkAll = async () => {
    if (unreadCount <= 0 || marking) return;
    setMarking(true);
    try {
      const res = await markNotificationsRead({ mark_all: true });
      setUnreadCount(res.unread_count);
      setItems((prev) => prev.map((row) => ({ ...row, is_read: true })));
    } catch {
      // ignore
    } finally {
      setMarking(false);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        aria-label="Notifications"
        sx={{
          cursor: "pointer",
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 0.5,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
            fill={active || open ? "#0a829d" : "#065B70"}
          />
        </svg>
        {unreadCount > 0 ? (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 10,
              minWidth: 16,
              height: 16,
              borderRadius: "999px",
              backgroundColor: "#d92d20",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              px: "3px",
              fontFamily: '"Rethink Sans", sans-serif',
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </Box>
        ) : null}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 1,
            width: 360,
            maxHeight: 440,
            borderRadius: 2,
            border: "1px solid #e4e7ec",
            boxShadow: "0 8px 24px rgba(3, 56, 67, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: "1px solid #eef2f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              color: "#033843",
            }}
          >
            Notifications{unreadCount ? ` (${unreadCount} unread)` : ""}
          </Typography>
          {unreadCount > 0 ? (
            <Button
              size="small"
              disabled={marking}
              onClick={() => void handleMarkAll()}
              sx={{
                textTransform: "none",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: 12,
                color: "#065b70",
                minWidth: 0,
                px: 1,
              }}
            >
              Mark all
            </Button>
          ) : null}
        </Box>

        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: "#065b70" }} />
            </Box>
          ) : items.length === 0 ? (
            <Typography
              sx={{
                p: 2,
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: 13,
                color: "#667085",
              }}
            >
              No notifications yet.
            </Typography>
          ) : (
            items.map((n) => (
              <Box
                key={n.id}
                onClick={() => handleItemClick(n)}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderBottom: "1px solid #f2f4f7",
                  cursor: "pointer",
                  backgroundColor: n.is_read ? "#fff" : "#f0fbfd",
                  display: "flex",
                  gap: 1,
                  "&:hover": { backgroundColor: n.is_read ? "#f5fbfc" : "#e6fafc" },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    mt: 0.75,
                    flexShrink: 0,
                    backgroundColor: n.is_read ? "transparent" : "#0a829d",
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#065b70",
                      mb: 0.25,
                    }}
                  >
                    {notificationVariantLabel(n.variant)}
                    {n.created_at ? ` · ${formatWhen(n.created_at)}` : ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 13,
                      fontWeight: n.is_read ? 500 : 700,
                      color: "#033843",
                    }}
                  >
                    {n.title || "Notification"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 12,
                      color: "#475467",
                      mt: 0.25,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {n.body}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ p: 1, borderTop: "1px solid #eef2f6" }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              handleClose();
              navigate("/dashboard/notifications");
            }}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              color: "#065b70",
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
